import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { formatINR, formatNum, STATUS_STYLES, CHART_COLORS } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  TrendingUp, Users, Target, Activity, AlertTriangle, Globe, Flag, Percent,
  ArrowUpRight, ChevronRight,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";

const KPI_META = [
  { key: "total_revenue", label: "Total Revenue", icon: TrendingUp, fmt: formatINR, accent: "text-emerald-600", hero: true },
  { key: "total_leads", label: "Total Leads", icon: Users, fmt: formatNum, accent: "text-sky-600", hero: true },
  { key: "total_conversions", label: "Conversions", icon: Target, fmt: formatNum, accent: "text-violet-600", hero: true },
  { key: "conversion_rate", label: "Conversion Rate", icon: Percent, fmt: (v) => `${v}%`, accent: "text-primary", hero: true },
  { key: "ot_utilization", label: "OT Utilization", icon: Activity, fmt: (v) => `${v}%`, accent: "text-amber-600" },
  { key: "open_issues", label: "Open Issues", icon: AlertTriangle, fmt: formatNum, accent: "text-rose-600" },
  { key: "sites_down", label: "Sites Down", icon: Globe, fmt: formatNum, accent: "text-rose-600" },
  { key: "goals_on_track_pct", label: "Goals On Track", icon: Flag, fmt: (v) => `${v}%`, accent: "text-emerald-600" },
];

function ChartCard({ title, children, testid, action }) {
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-base font-semibold tracking-tight">{title}</h3>
        {action}
      </div>
      {children}
    </Card>
  );
}

function StatusTile({ name, status, metric, metricLabel, testid }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.green;
  return (
    <div className={`rounded-xl border p-3 flex items-center gap-3 ${s.card}`} data-testid={testid}>
      <span className={`h-2.5 w-2.5 rounded-full ${s.dot} shrink-0`} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold truncate">{name}</p>
        <p className="text-xs opacity-80">{metricLabel}: {metric}</p>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { centerFilter, dateRange } = useApp();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get("/dashboard/summary", { params: { range: dateRange, center: centerFilter } })
      .then((res) => { if (active) setData(res.data); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [centerFilter, dateRange]);

  const tooltipStyle = {
    backgroundColor: "hsl(var(--popover))",
    border: "1px solid hsl(var(--border))",
    borderRadius: "0.75rem",
    color: "hsl(var(--popover-foreground))",
    fontSize: "12px",
  };

  return (
    <div className="space-y-6" data-testid="ceo-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">CEO Command Center</p>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">
            Good day, {user?.name?.split(" ")[0]}
          </h1>
        </div>
        <Badge variant="secondary" className="w-fit gap-1.5 py-1.5 px-3">
          <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
          Live · {dateRange}
        </Badge>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {loading
          ? Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)
          : KPI_META.map((m, i) => {
              const Icon = m.icon;
              const val = data?.kpis?.[m.key] ?? 0;
              return (
                <Card
                  key={m.key}
                  className="p-4 sm:p-5 kpi-card-enter hover:shadow-md transition-shadow duration-200"
                  style={{ animationDelay: `${i * 50}ms` }}
                  data-testid={`kpi-card-${m.key.replace(/_/g, "-")}`}
                >
                  <div className="flex items-start justify-between">
                    <div className={`h-9 w-9 rounded-xl bg-secondary flex items-center justify-center ${m.accent}`}>
                      <Icon className="h-[18px] w-[18px]" />
                    </div>
                    {m.hero && <ArrowUpRight className="h-4 w-4 text-muted-foreground/40" />}
                  </div>
                  <p className="mt-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">{m.label}</p>
                  <p className="mt-1 font-display text-xl sm:text-2xl font-extrabold tracking-tight tabular-nums">
                    {m.key === "sites_down" ? `${val} / ${data?.kpis?.sites_total ?? 0}` : m.fmt(val)}
                  </p>
                </Card>
              );
            })}
      </div>

      {/* Attention + Center health */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-5" data-testid="attention-list">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="h-5 w-5 text-rose-500" />
            <h3 className="font-display text-base font-semibold tracking-tight">Today's Attention</h3>
          </div>
          <div className="space-y-2">
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-xl" />)
            ) : data?.attention?.length ? (
              data.attention.map((a, i) => {
                const s = STATUS_STYLES[a.severity] || STATUS_STYLES.amber;
                return (
                  <div key={i} className={`rounded-xl border p-3 flex items-center gap-3 ${s.card}`} data-testid={`attention-item-${i}`}>
                    <span className={`h-2 w-2 rounded-full ${s.dot} shrink-0`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold truncate">{a.title}</p>
                      <p className="text-xs opacity-80 truncate">{a.detail}</p>
                    </div>
                    <Badge className={`${s.badge} border-0 text-[10px] shrink-0`}>{s.label}</Badge>
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-muted-foreground py-6 text-center">Nothing needs your attention. 🎉</p>
            )}
          </div>
        </Card>

        <Card className="p-5" data-testid="center-health">
          <h3 className="font-display text-base font-semibold tracking-tight mb-4">Center Health</h3>
          <div className="space-y-2">
            {loading
              ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-xl" />)
              : data?.center_health?.map((c) => (
                  <StatusTile key={c.id} name={c.center_name} status={c.status} metric={`${c.score}/100`}
                    metricLabel="Health score" testid={`center-status-${c.center_code}`} />
                ))}
          </div>
        </Card>
      </div>

      {/* Charts row 1 */}
      <div className="grid lg:grid-cols-3 gap-4">
        <ChartCard title="Revenue Trend" testid="chart-revenue-trend" action={<TrendingUp className="h-4 w-4 text-emerald-500" />}>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={data?.revenue_trend || []}>
              <defs>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0D9488" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#0D9488" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                tickFormatter={(d) => d?.slice(5)} minTickGap={24} />
              <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                tickFormatter={(v) => `${(v / 1e5).toFixed(0)}L`} width={36} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => [formatINR(v), "Revenue"]} />
              <Area type="monotone" dataKey="revenue" stroke="#0D9488" strokeWidth={2} fill="url(#revGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Leads by Source" testid="chart-leads-by-source">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={data?.leads_by_source || []} dataKey="value" nameKey="source" cx="50%" cy="50%"
                innerRadius={48} outerRadius={80} paddingAngle={2}>
                {(data?.leads_by_source || []).map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Conversion Funnel" testid="chart-conversion-funnel">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data?.conversion_funnel || []} layout="vertical" margin={{ left: 12 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis type="category" dataKey="stage" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} width={92} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => [formatNum(v), "Count"]} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={28}>
                {(data?.conversion_funnel || []).map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Charts row 2 + dept health */}
      <div className="grid lg:grid-cols-3 gap-4">
        <ChartCard title="Center Comparison — Revenue" testid="chart-center-comparison" action={<span className="text-xs text-muted-foreground">All centers</span>}>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data?.center_comparison || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="center" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                tickFormatter={(v) => `${(v / 1e7).toFixed(1)}Cr`} width={40} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => [formatINR(v), "Revenue"]} />
              <Bar dataKey="revenue" radius={[6, 6, 0, 0]} barSize={44}>
                {(data?.center_comparison || []).map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <Card className="lg:col-span-1 p-5" data-testid="department-health">
          <h3 className="font-display text-base font-semibold tracking-tight mb-4">Department Health</h3>
          <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
            {loading
              ? Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-xl" />)
              : data?.department_health?.map((d) => (
                  <StatusTile key={d.id} name={d.department_name} status={d.status}
                    metric={`${d.utilization}%`} metricLabel="Utilization"
                    testid={`department-status-${d.department_name.toLowerCase().split(" ")[0]}`} />
                ))}
          </div>
        </Card>

        <Card className="p-5 flex flex-col" data-testid="quick-insight">
          <h3 className="font-display text-base font-semibold tracking-tight mb-4">At a Glance</h3>
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Leads in period</span>
              <span className="font-semibold tabular-nums">{formatNum(data?.kpis?.total_leads)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Procedures done</span>
              <span className="font-semibold tabular-nums">{formatNum(data?.kpis?.total_conversions)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Revenue</span>
              <span className="font-semibold tabular-nums">{formatINR(data?.kpis?.total_revenue)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Avg conversion</span>
              <span className="font-semibold tabular-nums">{data?.kpis?.conversion_rate}%</span>
            </div>
          </div>
          <div className="mt-auto pt-4 border-t border-border">
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              Period {data?.range?.start} → {data?.range?.end} <ChevronRight className="h-3 w-3" />
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
